/**
 * EmergencyContactDrawer — Add / Edit emergency contact form.
 *
 * Fields: First Name, Last Name, Relationship, Phone, Email
 *
 * Validation:
 *   - First and last name required
 *   - Phone: 10 digits (dashes optional), required
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const EmergencyContactDrawer = ({
  open, onClose, onSave, saving,
  contact, // null for add, object for edit
}) => {
  const isEdit = !!contact;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});

  // Populate form when drawer opens
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setFirstName(contact?.contact_first_name || '');
      setLastName(contact?.contact_last_name || '');
      setRelationship(contact?.relationship || '');
      setPhone(contact?.contact_phone || '');
      setEmail(contact?.contact_email || '');
      setErrors({});
    }
    prevOpen.current = open;
  });

  const namePattern = /^[0-9a-zA-Z_\- '.]+$/;
  const phonePattern = /^[0-9]{3}-?[0-9]{3}-?[0-9]{4}$/;

  const validate = useCallback(() => {
    const errs = {};
    if (!firstName.trim()) {
      errs.firstName = 'First name is required.';
    } else if (!namePattern.test(firstName.trim())) {
      errs.firstName = 'Only letters, numbers, spaces, hyphens, and apostrophes.';
    }
    if (!lastName.trim()) {
      errs.lastName = 'Last name is required.';
    } else if (!namePattern.test(lastName.trim())) {
      errs.lastName = 'Only letters, numbers, spaces, hyphens, and apostrophes.';
    }
    if (!phone.trim()) {
      errs.phone = 'Phone number is required.';
    } else if (!phonePattern.test(phone.trim())) {
      errs.phone = 'Phone must be 10 digits (dashes optional).';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [firstName, lastName, phone]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      contact_first_name: firstName.trim(),
      contact_last_name: lastName.trim(),
      contact_name: `${firstName.trim()} ${lastName.trim()}`,
      relationship: relationship.trim(),
      contact_phone: phone.trim(),
      contact_email: email.trim(),
      // Pass through for edit
      ...(isEdit ? { monitoring_contact_id: contact.monitoring_contact_id } : {}),
    });
  }, [validate, onSave, firstName, lastName, relationship, phone, email, isEdit, contact]);

  return (
    <div className={`dp-drawer${open ? ' is-open' : ''}`}>
      <div className="dp-drawer__header">
        <h2 className="dp-drawer__title">
          {isEdit ? 'Edit Emergency Contact' : 'Add Emergency Contact'}
        </h2>
        <button className="dp-drawer__close" onClick={onClose}>&#x2715;</button>
      </div>

      <form className="dp-drawer__body" onSubmit={handleSubmit}>
        {/* First Name */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">
            First Name <span className="dp-drawer__required">*</span>
          </label>
          <input
            type="text"
            className={`dp-drawer__input${errors.firstName ? ' is-invalid' : ''}`}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            autoFocus
          />
          {errors.firstName ? <p className="dp-drawer__error">{errors.firstName}</p> : null}
        </div>

        {/* Last Name */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">
            Last Name <span className="dp-drawer__required">*</span>
          </label>
          <input
            type="text"
            className={`dp-drawer__input${errors.lastName ? ' is-invalid' : ''}`}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
          />
          {errors.lastName ? <p className="dp-drawer__error">{errors.lastName}</p> : null}
        </div>

        {/* Relationship */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Relationship</label>
          <input
            type="text"
            className="dp-drawer__input"
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            placeholder="e.g. Spouse, Son, Daughter"
          />
        </div>

        {/* Phone */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">
            Phone <span className="dp-drawer__required">*</span>
          </label>
          <input
            type="text"
            className={`dp-drawer__input${errors.phone ? ' is-invalid' : ''}`}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="555-123-4567"
          />
          {errors.phone ? <p className="dp-drawer__error">{errors.phone}</p> : null}
        </div>

        {/* Email */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Email</label>
          <input
            type="email"
            className="dp-drawer__input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
          />
        </div>

        {/* Footer */}
        <div className="dp-drawer__footer">
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dp-btn dp-btn--primary" disabled={saving}>
            {saving ? 'Saving\u2026' : isEdit ? 'Save Changes' : 'Add Contact'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmergencyContactDrawer;
